import { useEffect, useState, type FC } from "react";
import { IntervalToggleContainer } from "../CronGenerator.style";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select, { type SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import React from "react";

type CronValues = {
    minute: string;
    hour: string;
    day: string;
    month: string;
    weekDay: string;
};

type DayProps = {
    setCronValues: React.Dispatch<React.SetStateAction<CronValues>>;
    clear: boolean;
};

const DayOptions = {
    EVERY: "Day",
    RANGE: "Range",
} as const;

const DayOfMonth: FC<DayProps> = ({ setCronValues, clear }) => {
    const [activeOption, setActiveOption] = useState<string>(DayOptions.EVERY);
    const [error, setError] = useState(false);
    const [prefixError, setPrefixError] = useState(false);
    const [day, setDay] = useState("*");
    const [prefixInterval, setPrefixInterval] = useState("*");
    const [dayRange, setDayRange] = useState({ from: "", to: "" });
    const [dayRangeError, setDayRangeError] = useState({
        fromError: false,
        toError: false,
    });
    const [startAtEnabled, setStartAtEnabled] = useState<boolean>(false);

    useEffect(() => {
        setDay("*");
        setPrefixInterval("*");
        setError(false);
        setPrefixError(false);
        setDayRange({ from: "", to: "" });
        setDayRangeError({ fromError: false, toError: false });
        setStartAtEnabled(false);
    }, [activeOption, clear]);

    const handleOptionSelect = (event: SelectChangeEvent) => {
        setActiveOption(event.target.value);
    };

    const validateDay = (event: React.ChangeEvent<HTMLInputElement>) => {
        const val = Number(event.target.value);
        if (event.target.value === "") {
            setDay("*");
            setError(false);
            setStartAtEnabled(false);
        } else if (val >= 1 && val <= 31) {
            setDay(event.target.value);
            setError(false);
            setStartAtEnabled(true);
        } else {
            setDay("*");
            setError(true);
            setStartAtEnabled(false);
        }
    };

    const addPrefix = (event: React.ChangeEvent<HTMLInputElement>) => {
        const val = Number(event.target.value);
        if (event.target.value === "") {
            setPrefixInterval("*");
            setPrefixError(false);
        } else if (val >= 1 && val <= 31) {
            setPrefixInterval(event.target.value);
            setPrefixError(false);
        } else {
            setPrefixInterval("*");
            setPrefixError(true);
        }
    };

    const handleFromChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const fromVal = Number(event.target.value);
        setDayRange((prev) => ({
            ...prev,
            from: event.target.value,
        }));
        const toNum = Number(dayRange.to);
        const fromError = !(fromVal >= 1 && fromVal <= 31);
        if (fromError) {
            setStartAtEnabled(false);
        } else {
            setStartAtEnabled(true);
        }
        setDayRangeError({
            fromError,
            toError: !(fromVal <= toNum && toNum <= 31),
        });
    };

    const handleToChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const toVal = Number(event.target.value);
        setDayRange((prev) => ({
            ...prev,
            to: event.target.value,
        }));
        const fromNum = Number(dayRange.from);
        setDayRangeError((prev) => ({
            ...prev,
            toError: !(fromNum <= toVal && toVal <= 31),
        }));
    };

    useEffect(() => {
        if (activeOption === DayOptions.EVERY) {
            const val =
                prefixInterval === "*" || prefixInterval === ""
                    ? day
                    : day === "*" || day === ""
                        ? day
                        : `${prefixInterval}/${day}`;
            setCronValues((prev) => ({
                ...prev,
                day: val,
            }));
        }
    }, [day, prefixInterval, activeOption, setCronValues]);

    useEffect(() => {
        if (activeOption === DayOptions.RANGE) {
            let rangeVal = "*";
            const from = Number(dayRange.from);
            const to = Number(dayRange.to);
            if (from === 0 && to === 0) {
                rangeVal = "*"
            }
            else if (!isNaN(from) && !isNaN(to) && from <= to && to <= 31) {
                rangeVal = `${from}-${to}`;
            }
            setCronValues((prev) => ({
                ...prev,
                day: rangeVal,
            }));
        }else {
            setCronValues((prev) => ({ ...prev, day: "*" }));
        }
    }, [dayRange, activeOption, setCronValues]);

    const renderSelector = () => {
        switch (activeOption) {
            case DayOptions.EVERY:
                return (
                    <>
                        <TextField
                            label="Days"
                            variant="standard"
                            sx={{ m: 1, width: "fit-content" }}
                            onChange={validateDay}
                            error={error}
                            helperText={error ? "1 to 31" : ""}
                            value={day === "*" ? "" : day}
                        />
                        <TextField
                            label="Start at"
                            variant="standard"
                            disabled={!startAtEnabled}
                            sx={{ m: 1, width: "fit-content" }}
                            onChange={addPrefix}
                            error={prefixError}
                            helperText={prefixError ? "1 to 31" : "*Days"}
                            value={prefixInterval === "*" ? "" : prefixInterval}
                        />
                    </>
                );
            case DayOptions.RANGE:
                return (
                    <>
                        <TextField
                            label="From"
                            variant="standard"
                            sx={{ m: 1, width: "fit-content" }}
                            value={dayRange.from}
                            onChange={handleFromChange}
                            error={dayRangeError.fromError}
                            helperText={dayRangeError.fromError ? "1 to 31" : ""}
                        />
                        <TextField
                            label="To"
                            variant="standard"
                            disabled={!startAtEnabled}
                            sx={{ m: 1, width: "fit-content" }}
                            value={dayRange.to}
                            onChange={handleToChange}
                            error={dayRangeError.toError}
                            helperText={
                                dayRangeError.toError
                                    ? `${Number(dayRange.from) < 31 ? dayRange.from : 1} to 31`
                                    : " "
                            }
                        />
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <IntervalToggleContainer style={{ alignItems: "start" }}>
            <FormControl variant="standard" sx={{ m: 1, minWidth: 150 }}>
                <InputLabel id="day-select-label">Day of Month</InputLabel>
                <Select
                    labelId="day-select-label"
                    value={activeOption}
                    onChange={handleOptionSelect}
                >
                    <MenuItem value={DayOptions.EVERY}>{DayOptions.EVERY}</MenuItem>
                    <MenuItem value={DayOptions.RANGE}>{DayOptions.RANGE}</MenuItem>
                </Select>
            </FormControl>
            {renderSelector()}
        </IntervalToggleContainer>
    );
};

export default DayOfMonth;
